import { z } from "zod";
import { taskTypeKey } from "../drizzle/taskTypes.js";
import { claiminiIdSchema } from "../zod/claiminiId.js";
import { CLAIMINI_ID_ENTITY_NAME, attachmentSchema, entitySchema, findHighestConfidenceEntity } from "./heidelbot-entities.js";
function assertWellFormedClaiminiId(entities, ctx) {
    const claiminiId = findHighestConfidenceEntity(entities, CLAIMINI_ID_ENTITY_NAME);
    if (claiminiId == null) {
        ctx.addIssue({ code: "custom", message: `entities must carry a ${CLAIMINI_ID_ENTITY_NAME} entity` });
        return;
    }
    const parsed = claiminiIdSchema.safeParse(claiminiId.value);
    if (!parsed.success) {
        ctx.addIssue({ code: "custom", message: `Invalid ${CLAIMINI_ID_ENTITY_NAME}: ${parsed.error.issues[0]?.message ?? "malformed"}` });
    }
}
/**
 * Heidelbot inbound contract (POST /tasks/pdr), one arm per pdr.cloud task type.
 *
 * The email contract ({@link import("./email-tasks.js").createEmailTaskSchema}) without the `email`
 * block and the top-level `categories`, plus the `pdr` block. pdr.cloud hands Heidelbot exact
 * values, so every confidence is 1. One Check Step component renders both channels.
 * Unlike the email contract, `entities` must carry a well-formed claiminiId: pdr.cloud
 * knows the incident exactly, so a missing or malformed id is a sender bug, not an AI misread.
 *
 * Each arm narrows `type` to its literal and gives `attachments` the description naming the
 * categories and entities its workflow needs.
 */
const pdrTaskBaseSchema = z.object({
    // Both arms override this. Declaring it here is what keeps `type` first in the published schema.
    type: z.enum([taskTypeKey.COST_ESTIMATE, taskTypeKey.INVOICE]).meta({
        description: "Task type determining workflow routing",
    }),
    priority: z.int().positive().meta({
        description: "Task priority (higher = more important). Required: ePort neither defaults nor rescores it.",
        example: 100,
    }),
    pdr: z
        .object({
        snapshotId: z.string().min(1).meta({
            description: "The pdr.cloud snapshot id of the cost estimate or invoice. Used as the task's dedup key, so a retry returns the existing task. Send a new snapshotId to create a new task.",
            example: "est_5f3c9a12",
        }),
        orderId: z.string().min(1).optional().meta({
            description: "The pdr.cloud order the snapshot belongs to. Stored, not read.",
            example: "ord_7d2e4b90",
        }),
        incomingOrderId: z.string().min(1).optional().meta({
            description: "The pdr.cloud incoming order the snapshot belongs to. Stored, not read.",
            example: "inc_3a8f1c77",
        }),
        sectionId: z.string().min(1).optional().meta({
            description: "The eClaim section the snapshot belongs to. Stored, not read.",
            example: "0f6a3c2e-4b7d-4e1a-9c5f-2d8b6a1e7f43",
        }),
        providedAtPdr: z.string().min(1).optional().meta({
            description: "Opaque value sent by pdr.cloud. Its meaning is not defined. Stored, not read.",
        }),
    })
        .meta({ description: "The pdr.cloud snapshot this task was created from, plus order context that is stored but not read" }),
    entities: z
        .array(entitySchema)
        .meta({
        description: `Top-level extracted entities; must carry the incident's ${CLAIMINI_ID_ENTITY_NAME}, exactly 4 characters, each a letter, digit, hyphen, underscore, or dot.`,
    })
        .superRefine(assertWellFormedClaiminiId),
});
/** The cost-estimate arm of {@link createPdrTaskSchema}, exposed so callers can extend it (e.g. the cost_estimate_check step's input schema). */
export const costEstimatePdrTaskSchema = pdrTaskBaseSchema
    .extend({
    type: z.literal(taskTypeKey.COST_ESTIMATE).meta({
        description: "Task type determining workflow routing",
        example: taskTypeKey.COST_ESTIMATE,
    }),
    attachments: z.array(attachmentSchema).min(1).meta({
        description: "Documents of the snapshot, the PDF first. That PDF carries the top category COST_ESTIMATE and the money and garage entities.",
    }),
})
    .describe("Request body for creating a pdr.cloud cost-estimate task (from Heidelbot)");
/** The invoice arm of {@link createPdrTaskSchema}, exposed so callers can extend it (e.g. the invoice_check step's input schema). */
export const invoicePdrTaskSchema = pdrTaskBaseSchema
    .extend({
    type: z.literal(taskTypeKey.INVOICE).meta({
        description: "Task type determining workflow routing",
        example: taskTypeKey.INVOICE,
    }),
    attachments: z
        .array(attachmentSchema)
        .min(1)
        .meta({
        description: "Documents of the snapshot, the PDF first. That PDF carries the top category DOCUMENT and the invoice header entities plus positions. " +
            'Invoice header entity names: "personalAccount", "documentNumber", "documentDate", "totalNet", "totalTax", ' +
            '"totalGross", "serviceProvisionFrom", "serviceProvisionTo". "documentDate", "serviceProvisionFrom" and ' +
            '"serviceProvisionTo" must be ISO "YYYY-MM-DD"; "totalNet", "totalTax" and "totalGross" are money in integer ' +
            "cents. Neither rule is enforced here — a malformed value is not rejected, it silently produces a wrong value downstream.",
    }),
})
    .describe("Request body for creating a pdr.cloud invoice task (from Heidelbot)");
export const createPdrTaskSchema = z.discriminatedUnion("type", [costEstimatePdrTaskSchema, invoicePdrTaskSchema]);
